import { useImmer } from "use-immer";
import { useEffect } from "react";
import ApiConnector from "../connector/ApiConnector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faFloppyDisk } from "@fortawesome/free-regular-svg-icons";

const Component = () => {
  const newPanTemplate = { pan: "", merchantId: "", expirationMinutes: 30, isNew: true };
  const [vulnerablePans, updateVulnerablePans] = useImmer([]);
  const [baselineVulnerablePans, updateBaselineVulnerablePans] = useImmer([]);

  useEffect(() => {
    loadVulnerablePans();
  }, []);

  const loadVulnerablePans = async () => {
    const data = await ApiConnector.getVulnerablePans();
    if (data) {
      updateVulnerablePans(data);
      updateBaselineVulnerablePans(data);
    }
  };

  const hasChanged = () => {
    if (vulnerablePans.length !== baselineVulnerablePans.length) return true;
    const currentIds = vulnerablePans.filter(vp => !vp.isNew).map(vp => vp.id).sort();
    const baselineIds = baselineVulnerablePans.map(vp => vp.id).sort();
    if (JSON.stringify(currentIds) !== JSON.stringify(baselineIds)) return true;
    return vulnerablePans.some(vp => vp.isNew);
  };

  const canSave = hasChanged();

  const addNewRow = () => {
    updateVulnerablePans((d) => {
      d.unshift({ ...newPanTemplate });
    });
  };

  const save = async () => {
    if (!canSave) return;

    const ok = window.confirm("Save changes?");
    if (!ok) return;

    const baselineIds = baselineVulnerablePans.map(vp => vp.id);
    const currentIds = vulnerablePans.filter(vp => !vp.isNew).map(vp => vp.id);

    // Delete removed
    for (const id of baselineIds) {
      if (!currentIds.includes(id)) {
        await ApiConnector.deleteVulnerablePan(id);
      }
    }

    // Create new
    for (const vp of vulnerablePans) {
      if (vp.isNew && vp.pan) {
        await ApiConnector.createVulnerablePan(vp.pan, vp.merchantId, vp.expirationMinutes);
      }
    }

    await loadVulnerablePans();
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "-";
    return new Date(dateTime).toLocaleString();
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1 className="header-title">Vulnerable PANs</h1>
        <span className="header-save">
          <FontAwesomeIcon
            icon={faFloppyDisk}
            size="2xl"
            className="icon-clickable"
            onClick={canSave ? save : undefined}
            title={canSave ? "Save changes" : "No changes to save"}
            style={{
              opacity: canSave ? 1 : 0.4,
              cursor: canSave ? "pointer" : "not-allowed",
              pointerEvents: canSave ? "auto" : "none",
            }}
            aria-disabled={!canSave}
          />
        </span>
      </header>
      <main>
        <table className="config-table table-with-button">
          <thead>
            <tr>
              <th style={{ width: "auto" }}>PAN</th>
              <th style={{ width: "auto" }}>Merchant</th>
              <th style={{ width: "auto" }}>Source transaction ID</th>
              <th style={{ width: "auto" }}>Created</th>
              <th>
                <FontAwesomeIcon
                  icon={faPlus}
                  className="icon-clickable"
                  onClick={() => addNewRow()}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {vulnerablePans.map((vp, index) =>
              vp.isNew ? (
                <tr key={`new-${index}`}>
                  <td>
                    <input
                      type="text"
                      value={vp.pan}
                      onChange={(e) =>
                        updateVulnerablePans((d) => {
                          d[index].pan = e.target.value;
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={vp.merchantId}
                      onChange={(e) =>
                        updateVulnerablePans((d) => {
                          d[index].merchantId = e.target.value;
                        })
                      }
                    />
                  </td>
                  <td>NONE - manually added</td>
                  <td>-</td>
                  <td>
                    <FontAwesomeIcon
                      icon={faTrashCan}
                      className="icon-clickable"
                      onClick={() =>
                        updateVulnerablePans((d) => {
                          d.splice(index, 1);
                        })
                      }
                    />
                  </td>
                </tr>
              ) : (
                <tr key={vp.id}>
                  <td>{vp.pan}</td>
                  <td>{vp.merchantId || "-"}</td>
                  <td>{vp.sourceTransactionId || "NONE - manually added"}</td>
                  <td>{formatDateTime(vp.created)}</td>
                  <td>
                    <FontAwesomeIcon
                      icon={faTrashCan}
                      className="icon-clickable"
                      onClick={() =>
                        updateVulnerablePans((d) => {
                          d.splice(index, 1);
                        })
                      }
                    />
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </main>
    </>
  );
};

export default Component;
